// A member's saved views of a collection (phase 3 §5.1): one store for the
// page that saves them and the sidebar that lists them, so a collection's
// views are read once (GET /inventories/{id}/views) and every change shows
// everywhere at once. Views are personal: the store holds one account's
// (setSavedViewsUser) and is emptied on logout or when the session ends.
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import api from '../config/api';
import { canonicalQuery } from '../components/collection/listQuery';

const EMPTY = Object.freeze({ views: null, limit: 50, error: null, stale: true });
const entries = new Map();   // inventory id (string) => {views, limit, error, stale}
const pending = new Map();   // inventory id => the request under way
const versions = new Map();  // inventory id => bumped when its views are to be read again
const listeners = new Set();
// The account the store holds views for; bumped (with `epoch`) when it changes or is forgotten,
// so an answer to a request sent before cannot land in it.
let owner = null;
let epoch = 0;
const RETRIES = [5000, 20000];

const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const set = (key, changes) => {
  entries.set(key, { ...(entries.get(key) || EMPTY), ...changes });
  emit();
};

/**
 * Reads a collection's views. An answer is dropped when the store was
 * emptied (logout, another account) or the collection's views were marked
 * to read again meanwhile. A failure keeps what was shown, stays marked to
 * read again, and is retried a couple of times while someone shows them.
 */
function load(key, attempt = 0) {
  if (pending.has(key)) return pending.get(key);
  const sentAt = epoch;
  const version = versions.get(key) || 0;
  const current = () => sentAt === epoch && (versions.get(key) || 0) === version;
  const request = api.get(`/inventories/${key}/views`)
    .then((response) => {
      if (!current()) return;
      set(key, { views: response.data.views || [], limit: response.data.limit || 50, error: null, stale: false });
    })
    // The page works without them (the collection gone, a release under way).
    .catch((error) => {
      if (!current()) return;
      set(key, { views: (entries.get(key) || EMPTY).views || [], error, stale: true });
      if (attempt < RETRIES.length) {
        setTimeout(() => {
          if (current() && listeners.size > 0 && (entries.get(key) || EMPTY).stale) load(key, attempt + 1);
        }, RETRIES[attempt]);
      }
    })
    .finally(() => { if (pending.get(key) === request) pending.delete(key); });
  pending.set(key, request);
  return request;
}

/** Forgets every collection's views (a logout, a session gone: another member may log in next). */
export function forgetSavedViews() {
  epoch += 1;
  owner = null;
  entries.clear();
  pending.clear();
  emit();
}

/** The account whose views the store holds: another one empties it first. */
export function setSavedViewsUser(userId) {
  const next = userId === null || userId === undefined ? null : String(userId);
  if (next === owner) return;
  forgetSavedViews();
  owner = next;
}

/**
 * Reads a collection's views again (every collection's read so far, without
 * an id): what the server keeps of them changes when fields, types or places
 * do. Shown views stay on screen until the new answer.
 */
export function invalidateSavedViews(inventoryId) {
  const keys = inventoryId === undefined || inventoryId === null ? [...entries.keys()] : [String(inventoryId)];
  keys.forEach((key) => {
    versions.set(key, (versions.get(key) || 0) + 1);
    pending.delete(key);
    if (entries.has(key)) entries.set(key, { ...entries.get(key), stale: true, error: null });
  });
  emit();
  // Collections already read this session are read again at once (the old request, if any, no longer counts).
  keys.forEach((key) => { if (entries.has(key)) load(key); });
}

/** Reads a collection's views again now; resolves with them (null if they could not be read). */
export function refreshSavedViews(inventoryId) {
  const key = String(inventoryId);
  invalidateSavedViews(key);
  return load(key).then(() => {
    const entry = entries.get(key);
    return entry && !entry.stale ? entry.views : null;
  });
}

const ordered = (views) => [...views].sort((a, b) => a.position - b.position || a.id - b.id);

/**
 * The caller's views of a collection — `views` null until read — and what
 * changes them; each change resolves with the server's answer and rejects
 * with its error (409 view_exists, view_limit…), the store left as it was.
 */
export function useSavedViews(inventoryId) {
  const key = inventoryId === null || inventoryId === undefined ? null : String(inventoryId);
  const entry = useSyncExternalStore(subscribe, () => (key && entries.get(key)) || EMPTY);
  const generation = useSyncExternalStore(subscribe, () => epoch);
  // Read when first shown, again once marked stale or the store emptied (a failure is retried by load itself).
  const toRead = !!key && entry.stale && !entry.error;
  useEffect(() => {
    if (toRead) load(key);
  }, [key, toRead, generation]);

  const views = entry.views;
  const replace = useCallback((view) => {
    const current = (entries.get(key) || EMPTY).views || [];
    set(key, { views: ordered(current.some((v) => v.id === view.id)
      ? current.map((v) => (v.id === view.id ? view : v)) : [...current, view]) });
    return view;
  }, [key]);

  const create = useCallback((body) => api.post(`/inventories/${key}/views`, body)
    .then((response) => replace(response.data.view)), [key, replace]);
  const update = useCallback((id, body) => api.put(`/views/${id}`, body)
    .then((response) => replace(response.data.view)), [replace]);
  const remove = useCallback((id) => api.delete(`/views/${id}`).then(() => {
    set(key, { views: ((entries.get(key) || EMPTY).views || []).filter((v) => v.id !== id) });
  }), [key]);
  // One place up or down: the views between take their index as position. A
  // failure half-way reads the views again, so the order shown is the server's.
  const move = useCallback(async (id, delta) => {
    const list = [...((entries.get(key) || EMPTY).views || [])];
    const from = list.findIndex((v) => v.id === id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= list.length) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    const changed = list.map((v, index) => ({ v, index })).filter(({ v, index }) => v.position !== index);
    try {
      for (const { v, index } of changed) {
        // eslint-disable-next-line no-await-in-loop
        await update(v.id, { position: index });
      }
    } catch (error) {
      refreshSavedViews(key);
      throw error;
    }
  }, [key, update]);

  return { views, limit: entry.limit, error: entry.error, create, update, remove, move };
}

/** Where a view opens: its collection's list with its query, its mode and a notice for what it lost. */
export const viewLink = (inventoryId, view) => ({
  to: { pathname: `/c/${inventoryId}`, search: view.query ? `?${view.query}` : '' },
  state: { savedView: view.id, viewMode: view.view || null, dropped: (view.dropped || []).length },
});

/**
 * The view the list shows now: the one opened (the address's state says
 * which, kept while its list is changed), else one whose query is exactly
 * the address's. `modified` when the list moved away from the opened view's
 * query, or the view lost parts since it was saved (updating it forgets them).
 * @param {{search: string, state: any}} location
 */
export function activeView(views, location, inventory) {
  if (!views || views.length === 0) return { view: null, modified: false };
  const current = canonicalQuery(location.search || '', inventory);
  const opened = location.state && location.state.savedView
    ? views.find((view) => view.id === location.state.savedView) : null;
  if (opened) {
    return { view: opened, modified: canonicalQuery(opened.query, inventory) !== current || (opened.dropped || []).length > 0 };
  }
  const same = current === '' ? null : views.find((view) => canonicalQuery(view.query, inventory) === current);
  return { view: same || null, modified: false };
}
